import ENDPOINTS from 'api/endpoints';
import { doDelete, doGet, doPatch, doPost } from 'api/request';
import { LOGGER_TYPE } from 'api/types';
import { generateNextPath } from 'lib/routeHelper';
import type {
  ExternalSourceType,
  SourceHubDeploymentStatusResponse,
  SourceHubDeploymentsResponse,
  SourceHubExternalSourceRequest,
  SourceHubSourcesResponse,
  SourceHubUpdateSourceRequest,
} from './types';

export const getSourceHubSources = ({
  accountId,
  projectId,
}: {
  accountId: string;
  projectId: string;
}) => {
  return doGet<SourceHubSourcesResponse>(
    generateNextPath(`${ENDPOINTS.SOURCE_HUB}/sources`, {
      accountId,
      projectId,
    }),
    LOGGER_TYPE.JUPITER,
    { version: 'v2' },
  );
};

export const createSourceHubFile = ({
  accountId,
  projectId,
  file,
}: {
  accountId: string;
  projectId: string;
  file: File;
}) => {
  const formData = new FormData();
  const blob = new Blob([file], { type: file.type });
  formData.append('files', blob, file.name);

  return doPost<SourceHubSourcesResponse>(
    generateNextPath(`${ENDPOINTS.SOURCE_HUB}/sources/files`, {
      accountId,
      projectId,
    }),
    LOGGER_TYPE.JUPITER,
    formData,
    {
      postingFile: true,
      parseResponseToJSON: true,
      version: 'v2',
    },
  );
};

export const updateSourceHubFile = ({
  accountId,
  projectId,
  sourceId,
  file,
}: {
  accountId: string;
  projectId: string;
  sourceId: number;
  file: File;
}) => {
  const formData = new FormData();
  const blob = new Blob([file], { type: file.type });
  formData.append('file', blob, file.name);

  return doPost<SourceHubSourcesResponse>(
    generateNextPath(`${ENDPOINTS.SOURCE_HUB}/sources/files/:sourceId`, {
      accountId,
      projectId,
      sourceId: sourceId.toString(),
    }),
    LOGGER_TYPE.JUPITER,
    formData,
    {
      postingFile: true,
      parseResponseToJSON: true,
      version: 'v2',
    },
  );
};

export const updateSourceHubSource = ({
  accountId,
  projectId,
  sourceId,
  update,
}: {
  accountId: string;
  projectId: string;
  sourceId: number;
  update: SourceHubUpdateSourceRequest;
}) => {
  return doPatch<SourceHubSourcesResponse>(
    generateNextPath(`${ENDPOINTS.SOURCE_HUB}/sources/:sourceId`, {
      accountId,
      projectId,
      sourceId: sourceId.toString(),
    }),
    LOGGER_TYPE.JUPITER,
    update,
    { version: 'v2' },
  );
};

export const deleteSourceHubSource = ({
  accountId,
  projectId,
  sourceId,
}: {
  accountId: string;
  projectId: string;
  sourceId: number;
}) => {
  return doDelete<SourceHubSourcesResponse>(
    generateNextPath(`${ENDPOINTS.SOURCE_HUB}/sources/:sourceId`, {
      accountId,
      projectId,
      sourceId: sourceId.toString(),
    }),
    LOGGER_TYPE.JUPITER,
    {},
    { version: 'v2' },
  );
};

export const getSourceHubDeployments = ({
  accountId,
  projectId,
}: {
  accountId: string;
  projectId: string;
}) => {
  return doGet<SourceHubDeploymentsResponse>(
    generateNextPath(`${ENDPOINTS.SOURCE_HUB}/deployments`, {
      accountId,
      projectId,
    }),
    LOGGER_TYPE.JUPITER,
    { version: 'v2' },
  );
};

export const createSourceHubDeployment = ({
  accountId,
  projectId,
  clientEnvs,
}: {
  accountId: string;
  projectId: string;
  clientEnvs: string[];
}) => {
  return doPost<SourceHubDeploymentsResponse>(
    generateNextPath(`${ENDPOINTS.SOURCE_HUB}/deployments`, {
      accountId,
      projectId,
    }),
    LOGGER_TYPE.JUPITER,
    {
      client_envs: clientEnvs,
    },
    { version: 'v2' },
  );
};

export const getSourceHubDeploymentStatus = ({
  accountId,
  projectId,
  deploymentId,
}: {
  accountId: string;
  projectId: string;
  deploymentId: number;
}) => {
  return doGet<SourceHubDeploymentStatusResponse>(
    generateNextPath(
      `${ENDPOINTS.SOURCE_HUB}/deployments/status/:deploymentId`,
      {
        accountId,
        projectId,
        deploymentId: deploymentId.toString(),
      },
    ),
    LOGGER_TYPE.JUPITER,
    { version: 'v2' },
  );
};

export const createSourceHubExternalSource = ({
  accountId,
  projectId,
  externalSourceType,
  externalSource,
}: {
  accountId: string;
  projectId: string;
  externalSourceType: ExternalSourceType;
  externalSource: SourceHubExternalSourceRequest;
}) => {
  return doPost<SourceHubSourcesResponse>(
    generateNextPath(`${ENDPOINTS.SOURCE_HUB}/sources/:externalSourceType`, {
      accountId,
      projectId,
      externalSourceType,
    }),
    LOGGER_TYPE.JUPITER,
    externalSource,
    { version: 'v2' },
  );
};

export const syncSourceHubExternalSource = ({
  accountId,
  projectId,
  sourceId,
}: {
  accountId: string;
  projectId: string;
  sourceId: number;
  externalSourceType: ExternalSourceType;
}) => {
  return doPost<SourceHubSourcesResponse>(
    generateNextPath(`${ENDPOINTS.SOURCE_HUB}/sync/:sourceId`, {
      accountId,
      projectId,
      sourceId: sourceId.toString(),
    }),
    LOGGER_TYPE.JUPITER,
    {},
    { version: 'v2' },
  );
};
